import { quoteFinalPrice, type QuoteItem, type UpdateQuotePayload } from '@performance-crm/shared';
import { Loader2 } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toast-context';
import { ApiRequestError } from '@/lib/api';
import { fmt, fromLocalInput, toDateInput, todayInTz } from '@/lib/format';
import { useUpdateQuote } from '@/lib/queries';
import { useCompanyTimezone } from '@/lib/useCompanyTimezone';

interface Form {
  priceList: string;
  discount: string;
  price: string;
  expiresAt: string;
  notes: string;
}

const amount = (v: string | null) => (v === null || v === '' ? '' : String(Number(v)));

function formOf(q: QuoteItem, tz: string): Form {
  return {
    priceList: amount(q.priceList),
    discount: amount(q.discount),
    price: amount(q.price),
    expiresAt: toDateInput(q.expiresAt, tz),
    notes: q.notes ?? '',
  };
}

/**
 * Corregir una proforma vigente o expirada (Lote 5 · H4, P14, ux-comercial-04): precio de lista, descuento,
 * precio final, vigencia y notas. La unidad y la moneda no se cambian (se anula y se emite otra). Con precio de
 * lista, el final se recalcula como lista − descuento; sin lista se corrige el precio directo. Una expirada con
 * vigencia nueva vuelve a estar vigente. Solo viaja lo que cambió.
 */
export function EditQuoteDialog({
  companyId,
  quote,
  onOpenChange,
}: {
  companyId: string;
  quote: QuoteItem | null;
  onOpenChange: (o: boolean) => void;
}) {
  const tz = useCompanyTimezone(companyId);
  const mutation = useUpdateQuote();
  const toast = useToast();
  const [form, setForm] = useState<Form | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (quote) {
      setForm(formOf(quote, tz));
      setError(null);
    }
  }, [quote, tz]);
  if (!quote || !form) return null;
  const initial = formOf(quote, tz);
  const set = (patch: Partial<Form>) => setForm((f) => (f ? { ...f, ...patch } : f));
  const withList = quote.priceList !== null;
  const finalPrice = withList
    ? quoteFinalPrice(Number(form.priceList) || 0, Number(form.discount) || 0)
    : Number(form.price) || 0;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const input: UpdateQuotePayload = {};
    if (withList) {
      if (form.priceList !== initial.priceList) input.priceList = Number(form.priceList);
      if (form.discount !== initial.discount) input.discount = Number(form.discount) || 0;
    } else if (form.price !== initial.price) input.price = Number(form.price);
    if (form.expiresAt !== initial.expiresAt)
      // Vigente hasta el final de ese día en la zona de la empresa (como al emitir).
      input.expiresAt = form.expiresAt
        ? new Date(fromLocalInput(`${form.expiresAt}T23:59`, tz))
        : null;
    if (form.notes.trim() !== initial.notes) input.notes = form.notes.trim() || null;
    if (Object.keys(input).length === 0) {
      toast.success('No había nada que corregir');
      onOpenChange(false);
      return;
    }
    try {
      await mutation.mutateAsync({ id: quote.id, input });
      toast.success(`Se corrigió la proforma ${quote.code}`);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'No se pudo guardar la corrección');
    }
  };

  return (
    <Dialog open={!!quote} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>
              Corregir la proforma {quote.code}
              {quote.unitCode ? ` · ${quote.unitCode}` : ''}
            </DialogTitle>
            <DialogDescription>
              Cambia solo lo que estuvo mal: queda en la auditoría y en la actividad del lead. La
              unidad y la moneda no se corrigen aquí: si cambian, anula esta proforma y emite otra.
              {quote.status === 'EXPIRADA'
                ? ' Con una vigencia nueva la proforma vuelve a estar vigente.'
                : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-3">
            {withList ? (
              <>
                <div className="space-y-1">
                  <Label htmlFor="eq-list">Precio de lista</Label>
                  <Input
                    id="eq-list"
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.priceList}
                    onChange={(e) => set({ priceList: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="eq-discount">Descuento</Label>
                  <Input
                    id="eq-discount"
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.discount}
                    onChange={(e) => set({ discount: e.target.value })}
                  />
                </div>
              </>
            ) : (
              <div className="space-y-1 sm:col-span-2">
                <Label htmlFor="eq-price">Precio</Label>
                <Input
                  id="eq-price"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.price}
                  onChange={(e) => set({ price: e.target.value })}
                  required
                />
              </div>
            )}
            <div className="space-y-1">
              <Label>Precio final</Label>
              <p className="py-2 text-sm font-semibold" data-testid="edit-quote-final">
                {fmt.money(finalPrice, quote.currency)}
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="eq-expires">Vigente hasta</Label>
              <Input
                id="eq-expires"
                type="date"
                value={form.expiresAt}
                min={todayInTz(tz)}
                onChange={(e) => set({ expiresAt: e.target.value })}
              />
            </div>
            <div className="space-y-1 sm:col-span-3">
              <Label htmlFor="eq-notes">Notas</Label>
              <Textarea
                id="eq-notes"
                rows={2}
                value={form.notes}
                onChange={(e) => set({ notes: e.target.value })}
              />
            </div>
          </div>
          {error && (
            <p
              role="alert"
              className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm"
            >
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending} data-testid="edit-quote-submit">
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Guardar
              corrección
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
